// Ready-made sections. Their texts are i18n keys ({sec.*}) resolved at insertion; their images are
// SVG placeholders drawn here (nothing to license), to be replaced by the user's own pictures.
import { iconSvg } from '../blocks/basic.js';

/** A soft placeholder picture as a data URL. */
export function placeholder(width = 1200, height = 800, hue = 250) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`
        + `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="hsl(${hue},70%,92%)"/><stop offset="1" stop-color="hsl(${hue + 30},60%,80%)"/></linearGradient></defs>`
        + `<rect width="100%" height="100%" fill="url(#g)"/>`
        + `<g fill="hsl(${hue},45%,62%)" opacity=".75" transform="translate(${width / 2 - 60} ${height / 2 - 45})">`
        + `<circle cx="34" cy="26" r="14"/><path d="M0 90 38 48l26 26 22-20 34 36z"/></g></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const img = (alt, hue = 250, w = 1200, hgt = 800, extra = '') => `<figure class="figure image pflow-img w-100 mb-0"><img class="figure-img img-fluid rounded-4 w-100 ${extra}" style="aspect-ratio: ${w} / ${hgt}; object-fit: cover" src="${placeholder(w, hgt, hue)}" alt="${alt}"></figure>`;
const icon = (name, color = 'text-primary') => `<div class="pflow-icon ${color} mb-3" data-icon="${name}" style="font-size: 40px">${iconSvg(name)}</div>`;
const button = (label, style = 'btn-primary', extra = '') => `<a href="#" class="btn ${style} ${extra}">${label}</a>`;

// Schematic thumbnails of the palette (60×40).
const thumb = shapes => `<svg viewBox="0 0 60 40" width="60" height="40">${shapes}</svg>`;
const r = (x, y, w, hh, o = 1, rx = 1.5) => `<rect x="${x}" y="${y}" width="${w}" height="${hh}" rx="${rx}" fill="currentColor" opacity="${o}"/>`;

export const sectionTemplates = [
    {
        id: 'hero-split',
        label: 'sec.heroSplit',
        description: 'sec.heroSplitDesc',
        block: 'box',
        thumb: thumb(r(4, 10, 22, 4, 0.9) + r(4, 17, 26, 2, 0.4) + r(4, 21, 22, 2, 0.4) + r(4, 27, 9, 4, 0.8) + r(15, 27, 9, 4, 0.35) + r(33, 6, 23, 28, 0.25, 3)),
        html: `<section class="py-5"><div class="container"><div class="row align-items-center g-5">
<div class="col-12 col-lg-6"><h1 class="pflow-heading display-5 fw-bold">{sec.heroTitle}</h1><div class="pflow-editor"><p class="lead text-muted">{sec.heroText}</p></div><div class="pflow-button">${button('{sec.getStarted}', 'btn-primary', 'btn-lg')} ${button('{sec.learnMore}', 'btn-outline-secondary', 'btn-lg ms-2')}</div></div>
<div class="col-12 col-lg-6">${img('{sec.imageAlt}', 250, 1200, 900, 'shadow')}</div>
</div></div></section>`,
    },
    {
        id: 'hero-center',
        label: 'sec.heroCenter',
        description: 'sec.heroCenterDesc',
        block: 'box',
        thumb: thumb(r(0, 0, 60, 40, 0.12, 2) + r(14, 10, 32, 5, 0.9) + r(10, 18, 40, 2, 0.4) + r(16, 22, 28, 2, 0.4) + r(20, 28, 9, 4, 0.8) + r(31, 28, 9, 4, 0.35)),
        html: `<section class="py-5 bg-light text-center"><div class="container py-lg-5"><div class="row justify-content-center"><div class="col-12 col-lg-8">
<h1 class="pflow-heading display-4 fw-bold">{sec.centerTitle}</h1><div class="pflow-editor"><p class="lead text-muted">{sec.centerText}</p></div>
<div class="pflow-button">${button('{sec.getStarted}', 'btn-primary', 'btn-lg')} ${button('{sec.contactUs}', 'btn-outline-primary', 'btn-lg ms-2')}</div>
</div></div></div></section>`,
    },
    {
        id: 'features',
        label: 'sec.features',
        description: 'sec.featuresDesc',
        block: 'box',
        thumb: thumb(r(18, 4, 24, 4, 0.9) + [4, 23, 42].map(x => r(x, 14, 5, 5, 0.7, 2.5) + r(x, 22, 14, 3, 0.8) + r(x, 27, 14, 2, 0.35) + r(x, 31, 12, 2, 0.35)).join('')),
        html: `<section class="py-5"><div class="container">
<h2 class="pflow-heading text-center fw-bold">{sec.featuresTitle}</h2><div class="pflow-editor text-center mb-5"><p class="text-muted">{sec.featuresText}</p></div>
<div class="row g-4">${[['lightning', '{sec.feature1}'], ['shield-check', '{sec.feature2}'], ['device-mobile', '{sec.feature3}']].map(([name, title]) => `<div class="col-12 col-md-4">${icon(name)}<h3 class="pflow-heading h5 fw-semibold">${title}</h3><div class="pflow-editor"><p class="text-muted">{sec.featureText}</p></div></div>`).join('')}</div>
</div></section>`,
    },
    {
        id: 'image-text',
        label: 'sec.imageText',
        description: 'sec.imageTextDesc',
        block: 'box',
        thumb: thumb(r(4, 6, 24, 28, 0.25, 3) + r(33, 10, 22, 4, 0.9) + r(33, 17, 23, 2, 0.4) + r(33, 21, 20, 2, 0.4) + r(33, 27, 9, 4, 0.8)),
        html: `<section class="py-5"><div class="container"><div class="row align-items-center g-5">
<div class="col-12 col-md-6">${img('{sec.imageAlt}', 200, 1200, 1000)}</div>
<div class="col-12 col-md-6"><h2 class="pflow-heading fw-bold">{sec.imageTextTitle}</h2><div class="pflow-editor"><p class="text-muted">{sec.imageTextText}</p><ul><li>{sec.point1}</li><li>{sec.point2}</li><li>{sec.point3}</li></ul></div><div class="pflow-button">${button('{sec.learnMore}', 'btn-primary')}</div></div>
</div></div></section>`,
    },
    {
        id: 'cta',
        label: 'sec.cta',
        description: 'sec.ctaDesc',
        block: 'container',
        thumb: thumb(r(0, 8, 60, 24, 0.85, 2) + `<rect x="6" y="15" width="26" height="4" rx="1.5" fill="#fff"/><rect x="6" y="22" width="20" height="2" rx="1" fill="#fff" opacity=".7"/><rect x="42" y="16" width="12" height="7" rx="2" fill="#fff"/>`),
        html: `<div class="container-fluid bg-primary text-white py-5"><div class="container"><div class="row align-items-center g-4">
<div class="col-12 col-lg-8"><h2 class="pflow-heading fw-bold">{sec.ctaTitle}</h2><div class="pflow-editor"><p class="mb-0 opacity-75">{sec.ctaText}</p></div></div>
<div class="col-12 col-lg-4 text-lg-end"><div class="pflow-button">${button('{sec.getStarted}', 'btn-light', 'btn-lg')}</div></div>
</div></div></div>`,
    },
    {
        id: 'cards',
        label: 'sec.cards',
        description: 'sec.cardsDesc',
        block: 'box',
        thumb: thumb([4, 23, 42].map(x => r(x, 6, 14, 10, 0.25, 2) + r(x, 19, 12, 3, 0.85) + r(x, 24, 14, 2, 0.35) + r(x, 28, 10, 2, 0.35)).join('')),
        html: `<section class="py-5"><div class="container">
<h2 class="pflow-heading fw-bold mb-4">{sec.cardsTitle}</h2>
<div class="row g-4">${[230, 280, 320].map(hue => `<div class="col-12 col-md-4"><div class="pflow-card h-100 bg-white rounded-4 p-3 shadow-sm">${img('{sec.imageAlt}', hue, 1200, 800)}<h3 class="h5 mt-3">{sec.cardTitle}</h3><p class="text-muted">{sec.cardText}</p><div class="pflow-button"><a href="#" class="btn btn-outline-primary btn-sm">{sec.readMore}</a></div></div></div>`).join('')}</div>
</div></section>`,
    },
    {
        id: 'testimonials',
        label: 'sec.testimonials',
        description: 'sec.testimonialsDesc',
        block: 'box',
        thumb: thumb(r(0, 0, 60, 40, 0.1, 2) + [4, 23, 42].map(x => r(x, 8, 14, 24, 0.3, 2) + r(x + 2, 12, 10, 2, 0.6) + r(x + 2, 16, 9, 2, 0.6) + r(x + 2, 24, 7, 2, 0.9)).join('')),
        html: `<section class="py-5 bg-light"><div class="container">
<h2 class="pflow-heading text-center fw-bold mb-5">{sec.testimonialsTitle}</h2>
<div class="row g-4">${[1, 2, 3].map(n => `<div class="col-12 col-md-4"><div class="h-100 bg-white rounded-4 p-4 shadow-sm"><blockquote class="mb-3">“{sec.quote${n}}”</blockquote><p class="fw-semibold mb-0">{sec.name${n}}</p><p class="small text-muted mb-0">{sec.role${n}}</p></div></div>`).join('')}</div>
</div></section>`,
    },
    {
        id: 'pricing',
        label: 'sec.pricing',
        description: 'sec.pricingDesc',
        block: 'box',
        thumb: thumb([4, 23, 42].map((x, i) => r(x, 4, 14, 32, i === 1 ? 0.5 : 0.22, 2) + r(x + 3, 9, 8, 2, 0.8) + r(x + 3, 14, 6, 5, 0.9) + r(x + 3, 23, 8, 1.5, 0.5) + r(x + 3, 27, 8, 1.5, 0.5) + r(x + 2, 31, 10, 3, 0.9)).join('')),
        html: `<section class="py-5"><div class="container">
<h2 class="pflow-heading text-center fw-bold">{sec.pricingTitle}</h2><div class="pflow-editor text-center mb-5"><p class="text-muted">{sec.pricingText}</p></div>
<div class="row g-4 align-items-stretch">${[['{sec.plan1}', '9', 'btn-outline-primary', 'border'], ['{sec.plan2}', '29', 'btn-primary', 'border border-primary border-2 shadow'], ['{sec.plan3}', '79', 'btn-outline-primary', 'border']].map(([plan, price, style, frame]) => `<div class="col-12 col-md-4"><div class="h-100 rounded-4 p-4 ${frame}"><h3 class="pflow-heading h5 text-muted">${plan}</h3><h4 class="pflow-heading display-6 fw-bold">$${price}<small class="fs-6 text-muted fw-normal"> / {sec.month}</small></h4><div class="pflow-editor"><ul class="list-unstyled my-4"><li class="mb-2">✓ {sec.benefit1}</li><li class="mb-2">✓ {sec.benefit2}</li><li class="mb-2">✓ {sec.benefit3}</li></ul></div><div class="pflow-button d-grid">${button('{sec.choose}', style)}</div></div></div>`).join('')}</div>
</div></section>`,
    },
    {
        id: 'faq',
        label: 'sec.faq',
        description: 'sec.faqDesc',
        block: 'box',
        thumb: thumb(r(16, 4, 28, 4, 0.9) + [12, 19, 26, 33].map(y => r(8, y, 44, 5, 0.25, 1.5) + r(10, y + 1.5, 20, 2, 0.7)).join('')),
        html: `<section class="py-5"><div class="container"><div class="row justify-content-center"><div class="col-12 col-lg-8">
<h2 class="pflow-heading text-center fw-bold mb-4">{sec.faqTitle}</h2>
<div class="pflow-accordion">${[1, 2, 3, 4].map(n => `<details class="border rounded-3 mb-2 px-3 py-2"${n === 1 ? ' open' : ''}><summary class="fw-semibold py-1">{sec.question${n}}</summary><div class="pt-2"><p class="text-muted">{sec.answer${n}}</p></div></details>`).join('')}</div>
</div></div></div></section>`,
    },
    {
        id: 'stats',
        label: 'sec.stats',
        description: 'sec.statsDesc',
        block: 'box',
        thumb: thumb([3, 18, 33, 48].map(x => r(x, 13, 10, 7, 0.85) + r(x, 23, 10, 2, 0.4)).join('')),
        html: `<section class="py-5"><div class="container"><div class="row g-4 text-center">${[['12k', '{sec.stat1}'], ['98%', '{sec.stat2}'], ['24/7', '{sec.stat3}'], ['150+', '{sec.stat4}']].map(([value, label]) => `<div class="col-6 col-md-3"><h2 class="pflow-heading display-5 fw-bold text-primary mb-1">${value}</h2><div class="pflow-editor"><p class="text-muted mb-0">${label}</p></div></div>`).join('')}</div></div></section>`,
    },
    {
        id: 'team',
        label: 'sec.team',
        description: 'sec.teamDesc',
        block: 'box',
        thumb: thumb(r(18, 3, 24, 4, 0.9) + [6, 20, 34, 48].map(x => `<circle cx="${x + 3}" cy="19" r="5" fill="currentColor" opacity=".3"/>` + r(x - 1, 27, 8, 2, 0.8) + r(x, 31, 6, 1.5, 0.4)).join('')),
        html: `<section class="py-5"><div class="container">
<h2 class="pflow-heading text-center fw-bold mb-5">{sec.teamTitle}</h2>
<div class="row g-4 text-center">${[1, 2, 3, 4].map(n => `<div class="col-6 col-md-3"><figure class="figure image pflow-img d-table mx-auto" style="width: 60%"><img class="figure-img img-fluid rounded-circle" style="aspect-ratio: 1 / 1; object-fit: cover; width: 100%" src="${placeholder(600, 600, 200 + n * 25)}" alt="{sec.member} ${n}"></figure><h3 class="pflow-heading h6 fw-semibold mb-0">{sec.member} ${n}</h3><div class="pflow-editor"><p class="small text-muted">{sec.jobTitle}</p></div></div>`).join('')}</div>
</div></section>`,
    },
    {
        id: 'contact',
        label: 'sec.contact',
        description: 'sec.contactDesc',
        block: 'box',
        thumb: thumb(r(4, 8, 20, 4, 0.9) + r(4, 16, 18, 2, 0.4) + r(4, 21, 16, 2, 0.4) + r(4, 26, 18, 2, 0.4) + r(30, 6, 26, 28, 0.25, 3) + `<circle cx="43" cy="18" r="3" fill="currentColor" opacity=".8"/>`),
        html: `<section class="py-5"><div class="container"><div class="row g-5 align-items-center">
<div class="col-12 col-md-5"><h2 class="pflow-heading fw-bold">{sec.contactTitle}</h2><div class="pflow-editor"><p class="text-muted">{sec.contactText}</p><p><strong>{sec.addressLabel}</strong><br>{sec.address}</p><p><strong>{sec.phoneLabel}</strong><br><a href="tel:+15550100">+1 555 0100</a></p><p><strong>{sec.emailLabel}</strong><br><a href="mailto:hello@example.com">hello@example.com</a></p></div></div>
<div class="col-12 col-md-7"><figure class="figure pflow-map w-100"><iframe src="https://maps.google.com/maps?q=Eiffel%20Tower%2C%20Paris&amp;z=14&amp;output=embed" width="100%" height="380" loading="lazy" class="w-100 border-0 rounded-4" title="Map" referrerpolicy="no-referrer-when-downgrade"></iframe></figure></div>
</div></div></section>`,
    },
    {
        id: 'gallery',
        label: 'sec.gallery',
        description: 'sec.galleryDesc',
        block: 'box',
        thumb: thumb([4, 22, 40].map(x => r(x, 4, 16, 15, 0.3, 2) + r(x, 21, 16, 15, 0.2, 2)).join('')),
        html: `<section class="py-5"><div class="container">
<h2 class="pflow-heading fw-bold mb-4">{sec.galleryTitle}</h2>
<div class="row g-3">${[210, 240, 270, 300, 330, 180].map(hue => `<div class="col-6 col-md-4">${img('{sec.imageAlt}', hue, 1200, 900)}</div>`).join('')}</div>
</div></section>`,
    },
];
